import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePromptLogin } from '@/context/LoginModalContext';
import { Sparkles, Shirt, Star, MapPin, Clock, Phone, Plus, Minus, ArrowLeft, ShoppingBag, Wallet, Home } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ServiceProviderPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { role } = useAuth();
    const promptLogin = usePromptLogin();
    const [p, setP] = useState(null);
    const [qtys, setQtys] = useState({});
    const [form, setForm] = useState({
        kind: 'laundry',
        pickup_address: '',
        pickup_zone: '',
        pickup_date: '',
        pickup_slot: 'morning',
        delivery_date: '',
        delivery_slot: 'afternoon',
        notes: '',
    });
    const [busy, setBusy] = useState(false);
    const [walletBalance, setWalletBalance] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/services/providers/${id}`);
            setP(data);
            setForm(f => ({ ...f, kind: data.kind === 'drycleaning' ? 'drycleaning' : 'laundry' }));
        } catch (e) { toast.error('Prestador no disponible'); nav('/servicios'); }
    }, [id, nav]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (role === 'user') {
            api.get('/wallet/me').then(({ data }) => setWalletBalance(data.wallet.balance_mxn)).catch(() => {});
        }
    }, [role]);

    const changeQty = (key, delta) => setQtys(q => ({ ...q, [key]: Math.max(0, (q[key] || 0) + delta) }));

    const total = useMemo(() => {
        if (!p) return { subtotal: 0, pickup: 0, delivery: 0, total: 0 };
        let sub = 0;
        (p.pricelist || []).forEach(item => {
            const qty = qtys[item.key] || 0;
            sub += qty * item.price;
        });
        const pickup = sub > 0 ? (p.pickup_fee || 0) : 0;
        const delivery = sub > 0 ? (p.delivery_fee || 0) : 0;
        return { subtotal: sub, pickup, delivery, total: sub + pickup + delivery };
    }, [qtys, p]);

    const submit = async () => {
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'pay' });
            if (!ok) return;
        }
        if (total.subtotal < (p.min_order_amount || 0)) {
            toast.error(`Monto mínimo $${fmt(p.min_order_amount)}`);
            return;
        }
        if (!form.pickup_address || !form.pickup_date) {
            toast.error('Completa dirección y fecha de recolección');
            return;
        }
        const items = (p.pricelist || []).filter(it => qtys[it.key]).map(it => ({
            key: it.key, label: it.label, unit: it.unit, qty: qtys[it.key], price: it.price,
        }));
        if (items.length === 0) { toast.error('Elige al menos una prenda'); return; }
        setBusy(true);
        try {
            const { data } = await api.post('/services/orders', {
                provider_id: p.id,
                kind: form.kind,
                items,
                pickup_address: form.pickup_address,
                pickup_zone: form.pickup_zone,
                pickup_date: form.pickup_date,
                pickup_slot: form.pickup_slot,
                delivery_date: form.delivery_date || null,
                delivery_slot: form.delivery_slot || null,
                notes: form.notes || null,
            });
            toast.success('¡Servicio solicitado! Se cobró tu OkWallet.');
            nav('/dashboard/service-orders');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al solicitar');
        } finally { setBusy(false); }
    };

    if (!p) return <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>;

    const Icon = p.kind === 'drycleaning' ? Shirt : Sparkles;

    return (
        <div className="min-h-screen" data-testid="service-provider-page">
            <div className="max-w-6xl mx-auto px-4 py-6">
                <div className="flex items-center gap-2 mb-3">
                    <Link to="/servicios" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"><ArrowLeft className="h-4 w-4" />Volver a servicios</Link>
                    <span className="text-muted-foreground">·</span>
                    <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline" data-testid="svc-detail-home-btn"><Home className="h-4 w-4" />Inicio</Link>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: provider info + pricelist */}
                    <div className="lg:col-span-2 space-y-4">
                        <Card className="overflow-hidden">
                            <div className="h-32 bg-gradient-to-br from-[hsl(var(--primary))] to-red-700 relative">
                                {p.banner_url && <img src={p.banner_url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
                            </div>
                            <CardContent className="p-5">
                                <div className="flex items-start gap-4 -mt-14">
                                    <div className="h-16 w-16 rounded-2xl bg-card border-4 border-card overflow-hidden shadow">
                                        {p.logo_url ? <img src={p.logo_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center bg-[hsl(var(--accent))] font-semibold text-xl">{p.name?.[0]?.toUpperCase()}</div>}
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    <h1 className="text-2xl font-semibold">{p.name}</h1>
                                    <Badge><Icon className="h-3 w-3 mr-1" />{p.kind === 'both' ? 'Lavandería + Tintorería' : p.kind === 'laundry' ? 'Lavandería' : 'Tintorería'}</Badge>
                                </div>
                                <div className="text-xs text-muted-foreground">@{p.nickname}</div>
                                {p.description && <p className="text-sm mt-3">{p.description}</p>}
                                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mt-3">
                                    <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5" />{(p.rating_avg || 0).toFixed(1)} ({p.rating_count} reseñas)</span>
                                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Entrega en ~{p.turnaround_hours}h</span>
                                    {p.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{p.phone}</span>}
                                </div>
                                <div className="mt-3">
                                    <div className="text-xs uppercase text-muted-foreground mb-1">Zonas cubiertas</div>
                                    <div className="flex flex-wrap gap-1">
                                        {(p.zones || []).map(z => <Badge key={z} variant="outline">{z}</Badge>)}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-5">
                                <h2 className="text-lg font-semibold mb-3">Elige tus prendas</h2>
                                <div className="space-y-2">
                                    {(p.pricelist || []).map(item => (
                                        <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium">{item.label}</div>
                                                <div className="text-xs text-muted-foreground">${fmt(item.price)} / {item.unit === 'kg' ? 'kg' : item.unit === 'bundle' ? 'paquete' : 'pieza'}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => changeQty(item.key, -1)} data-testid={`qty-minus-${item.key}`}><Minus className="h-4 w-4" /></Button>
                                                <div className="w-10 text-center font-semibold" data-testid={`qty-value-${item.key}`}>{qtys[item.key] || 0}</div>
                                                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => changeQty(item.key, +1)} data-testid={`qty-plus-${item.key}`}><Plus className="h-4 w-4" /></Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-5 space-y-3">
                                <h2 className="text-lg font-semibold">Recolección y entrega</h2>
                                <div>
                                    <Label>Dirección de recolección</Label>
                                    <Textarea rows={2} value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} placeholder="Calle, número, colonia, referencias" data-testid="svc-pickup-addr" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div>
                                        <Label>Zona / CP</Label>
                                        <Input value={form.pickup_zone} onChange={(e) => setForm({ ...form, pickup_zone: e.target.value })} placeholder="06600" data-testid="svc-pickup-zone" />
                                    </div>
                                    <div>
                                        <Label>Fecha recolección</Label>
                                        <Input type="date" value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} data-testid="svc-pickup-date" />
                                    </div>
                                    <div>
                                        <Label>Horario</Label>
                                        <Select value={form.pickup_slot} onValueChange={(v) => setForm({ ...form, pickup_slot: v })}>
                                            <SelectTrigger data-testid="svc-pickup-slot"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {p.slots.includes('morning') && <SelectItem value="morning">Mañana (9-13)</SelectItem>}
                                                {p.slots.includes('afternoon') && <SelectItem value="afternoon">Tarde (14-18)</SelectItem>}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label>Fecha entrega (opcional)</Label>
                                        <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label>Horario entrega</Label>
                                        <Select value={form.delivery_slot} onValueChange={(v) => setForm({ ...form, delivery_slot: v })}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {p.slots.includes('morning') && <SelectItem value="morning">Mañana</SelectItem>}
                                                {p.slots.includes('afternoon') && <SelectItem value="afternoon">Tarde</SelectItem>}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label>Notas (opcional)</Label>
                                    <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ej: Cuidado con la seda, entrega en portería" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    {/* Right: sticky checkout */}
                    <div>
                        <Card className="sticky top-4">
                            <CardContent className="p-5">
                                <h3 className="font-semibold mb-3">Resumen</h3>
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between"><span>Subtotal prendas</span><span>${fmt(total.subtotal)}</span></div>
                                    <div className="flex justify-between text-muted-foreground"><span>Recolección</span><span>${fmt(total.pickup)}</span></div>
                                    <div className="flex justify-between text-muted-foreground"><span>Entrega</span><span>${fmt(total.delivery)}</span></div>
                                    <div className="border-t pt-2 mt-2 flex justify-between font-semibold text-base">
                                        <span>Total</span><span>${fmt(total.total)}</span>
                                    </div>
                                </div>
                                {walletBalance !== null && (
                                    <div className={`text-xs mt-3 p-2 rounded flex items-center gap-2 ${walletBalance >= total.total ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
                                        <Wallet className="h-3.5 w-3.5" />Tu OkWallet: ${fmt(walletBalance)}
                                        {walletBalance < total.total && <Link to="/dashboard/wallet" className="ml-auto underline">Recargar</Link>}
                                    </div>
                                )}
                                <Button className="w-full mt-4" onClick={submit} disabled={busy || total.total === 0} data-testid="svc-submit">
                                    <ShoppingBag className="h-4 w-4 mr-1" />{busy ? 'Procesando…' : `Solicitar por $${fmt(total.total)}`}
                                </Button>
                                <p className="text-[10px] text-muted-foreground mt-2 text-center">Pago con OkWallet · sin comisión</p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
